import React, { useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import Toggle from "@/components/admin/Toggle";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow text-charcoal/60" style={{ fontSize: 10 }}>
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

/**
 * Modal that captures the new-user form and posts to /api/admin/users.
 *
 * onClose() — backdrop close, no creation happened.
 * onCreated(user) — admin received a created user; the parent should also
 *                   surface the generated password (read from user._password).
 */
export default function UserCreateModal({ onClose, onCreated }) {
  // Crypto-light random initial password — admin can edit before submit.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(
    () =>
      Math.random().toString(36).slice(2, 6) +
      Math.random().toString(36).slice(2, 6),
  );
  const [role, setRole] = useState("member");
  const [showPrices, setShowPrices] = useState(false);
  const [active, setActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/admin/users", {
        email: email.trim().toLowerCase(),
        password,
        role,
        show_prices: showPrices,
        active,
      });
      onCreated({ ...data, _password: password });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create user.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ backgroundColor: "rgba(21,30,43,0.6)" }}
      data-testid="admin-new-user-modal"
    >
      <form
        onSubmit={onSubmit}
        className="bg-ivory w-full max-w-md mx-4 p-8 border border-mist"
      >
        <div className="flex items-baseline justify-between">
          <h3 className="font-display text-charcoal text-2xl">New user</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-charcoal/55 hover:text-charcoal"
          >
            <X size={18} />
          </button>
        </div>
        <span
          className="block bg-brass mt-4"
          style={{ width: 60, height: 1 }}
        />

        {error && (
          <p className="mt-5 text-sm" style={{ color: "#9D2D2E" }}>
            {error}
          </p>
        )}

        <div className="mt-6 space-y-5">
          <Field label="Email">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="new-user-email"
              className="w-full bg-transparent border-0 border-b border-mist focus:border-brass focus:outline-none text-charcoal pb-2"
              style={{ fontSize: "1rem" }}
            />
          </Field>
          <Field label="Password (auto-generated, copy now)">
            <input
              type="text"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="new-user-password"
              className="w-full bg-transparent border-0 border-b border-mist focus:border-brass focus:outline-none text-charcoal pb-2 font-mono"
              style={{ fontSize: "0.95rem" }}
            />
          </Field>
          <Field label="Role">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="new-user-role"
              className="w-full bg-transparent border-0 border-b border-mist focus:border-brass focus:outline-none text-charcoal pb-2"
              style={{ fontSize: "1rem" }}
            >
              <option value="member">member</option>
              <option value="admin">admin</option>
            </select>
          </Field>
          <div className="flex items-center justify-between pt-2">
            <span className="eyebrow text-charcoal/60">Show prices</span>
            <Toggle
              on={showPrices}
              onChange={setShowPrices}
              testid="new-user-show-prices"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="eyebrow text-charcoal/60">Active</span>
            <Toggle
              on={active}
              onChange={setActive}
              testid="new-user-active"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="btn-brass text-charcoal mt-8 w-full disabled:opacity-50"
          data-testid="new-user-submit"
        >
          {submitting ? "Creating…" : "Create user"}
        </button>
      </form>
    </div>
  );
}
